import { useState, useCallback } from 'react';
import { useFetch } from './useFetch';
import API_ROUTES from './apiRoutes';

interface User {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'member';
  createdAt: string;
  updatedAt: string;
}

interface UsersResponse {
  users: User[];
  count: number;
}

interface UsersState {
  users: User[];
  isLoading: boolean;
  error: string | null;
  totalCount: number;
}

export const useUsers = () => {
  const [usersState, setUsersState] = useState<UsersState>({
    users: [],
    isLoading: false,
    error: null,
    totalCount: 0,
  });

  const { fetchWithAuth } = useFetch();

  const fetchUsers = useCallback(async (search?: string, page?: number, size?: number) => {
    setUsersState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      // Construir query parameters
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (page) params.append('page', page.toString());
      if (size) params.append('size', size.toString());

      const queryString = params.toString();
      const url = queryString ? `${API_ROUTES.USERS.list()}?${queryString}` : API_ROUTES.USERS.list();

      const response = await fetchWithAuth(url, {
        method: 'GET',
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erro ao buscar usuários: ${response.status} - ${errorText}`);
      }

      const data: UsersResponse = await response.json();
      
      setUsersState({
        users: data.users,
        isLoading: false,
        error: null,
        totalCount: data.count,
      });

      return data.users;
    } catch (error) {
      let errorMessage = 'Erro desconhecido';
      
      if (error instanceof Error) {
        if (error.message.includes('Unauthorized')) {
          errorMessage = 'Sessão expirada. Faça login novamente.';
        } else if (error.message.includes('Unexpected token')) {
          errorMessage = 'Erro de comunicação com o servidor. Verifique se a API está funcionando.';
        } else {
          errorMessage = error.message;
        }
      }
      
      setUsersState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      throw error;
    }
  }, [fetchWithAuth]);

  const createUser = useCallback(async (name: string, email: string, role: 'admin' | 'member') => {
    try {
      const response = await fetchWithAuth(API_ROUTES.USERS.create(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, email, role }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erro ao criar usuário: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      let errorMessage = 'Erro ao criar usuário';
      
      if (error instanceof Error) {
        if (error.message.includes('already exists')) {
          errorMessage = 'Este email já está cadastrado.';
        } else {
          errorMessage = error.message;
        }
      }
      
      throw new Error(errorMessage);
    }
  }, [fetchWithAuth]);

  const updateUser = useCallback(async (userId: string, name?: string, role?: 'admin' | 'member') => {
    try {
      const response = await fetchWithAuth(API_ROUTES.USERS.update(userId), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, role }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Erro ao atualizar usuário: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      let errorMessage = 'Erro ao atualizar usuário';
      
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      throw new Error(errorMessage);
    }
  }, [fetchWithAuth]);

  return {
    fetchUsers,
    createUser,
    updateUser,
    usersState,
  };
};
